import React from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';

import Editor from '~/components/core/Editor';
import type { DisplayConfigProps, SuggestionOption } from '~/components/core/Editor/types';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import Text from '~/components/core/TextComponents/Text';
import { getHtmlElementInnerText } from '~/Utils';

interface EditorFormikProps {
  id: string;
  label?: React.ReactNode;
  disabled?: boolean;
  showOnly?: boolean;
  autoFocus?: boolean;
  maxHeight?: string;
  displayConfig?: DisplayConfigProps;
  suggestionOptions?: SuggestionOption[];
  classNameEditor?: string;
  hideContent?: boolean;
  sanitizeContent?: boolean;
}

const EditorFormik: React.FC<EditorFormikProps> = ({
  id,
  label,
  disabled,
  showOnly,
  autoFocus,
  maxHeight,
  displayConfig,
  suggestionOptions,
  classNameEditor,
  hideContent = false,
  sanitizeContent = false,
}) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const content = get(values, id);

  const handleChange = (value: string) => {
    setFieldValue(id, sanitizeContent ? getHtmlElementInnerText(value) : value);
  };

  return (
    <div>
      {label && (
        <Text className="pt-12" variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {label}
        </Text>
      )}
      {!showOnly ? (
        <div className="py-12">
          <Editor
            className={classNameEditor}
            disabled={disabled}
            onChange={handleChange}
            autoFocus={autoFocus}
            content={hideContent ? '' : content}
            displayConfig={
              !isEmpty(suggestionOptions) ? { ...(displayConfig || {}), suggestions: true } : displayConfig
            }
            suggestionOptions={suggestionOptions}
          />
          <ErrorHelperTextFormik id={id} />
        </div>
      ) : (
        <div style={{ maxHeight, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: content }} />
      )}
    </div>
  );
};

export default EditorFormik;
